import { FormConfig } from '@workwave-tidal/core/form'
import { useMemo } from 'react'

import { Tooltip } from '@/components/primitives/Tooltip'

import { Footer } from './components/Footer'
import { form } from './formState'
import { useOnSubmit } from './hooks/useOnSubmit'
import { useTexts } from './hooks/useTexts'
import { useValidateForm } from './hooks/useValidateForm'
import { DepotFormValues } from './types'
import { computeFormInitialValues } from './utils/computeFormInitialValues'
import { emptyDepotId } from './utils/createEmptyDepot'

type DepotFormProps = {
  depot?: uui.domain.client.rm.Depot
}

export function DepotForm(props: DepotFormProps) {
  const { depot } = props

  const { fields, title } = useTexts()
  const config = useMemo<FormConfig<DepotFormValues>>(
    () => ({
      id: 'depot-form',
      initialValues: computeFormInitialValues(depot),
    }),
    [depot],
  )

  const creating = config.initialValues.id === emptyDepotId

  const validateForm = useValidateForm()
  const onSubmit = useOnSubmit()

  return (
    <form.Form
      title={title}
      footer={<Footer />}
      onSubmit={onSubmit}
      onValidate={validateForm}
      config={config}
      requireTransaction
      stopEditingOnUnmount
      applyPendingFitMapOnMount
    >
      <form.TextField name="name" label={fields.name} testId="depot-name" />
      <form.ColorPicker name="color" label={fields.color} testId="depot-color" />

      <Tooltip title={fields.cannotEditField} placement="bottom" disabled={creating}>
        <form.LocationEditorField
          name="location"
          pinType="depot"
          snapToRoad={true}
          pinId="depotFormPin"
          ignoreMapBounds={false}
          label={fields.location}
          testId="depot-address"
          showAdjustAddress
          helperText={creating ? fields.locationHelper : undefined}
          placeholder={fields.locationPlaceholder}
          disabled={!creating}
        />
      </Tooltip>

      <form.Divider />

      <Tooltip title={fields.cannotEditField} placement="bottom" disabled={creating}>
        <form.CurrencyInput
          name="setupCost"
          label={fields.setupCost}
          helperText={creating ? fields.setupCostHelper : undefined}
          testId="depot-setupCost"
          disabled={!creating}
        />
      </Tooltip>

      <Tooltip title={fields.cannotEditField} placement="bottom" disabled={creating}>
        <form.DurationInput
          name="setupTime"
          label={fields.setupTime}
          helperText={creating ? fields.setupTimeHelper : undefined}
          testId="depot-setupTime"
          disabled={!creating}
        />
      </Tooltip>
    </form.Form>
  )
}
